<template>
  <base-modal
    name="editImageModal"
    :title="get('image').title || $t('gallery.edit_image_modal.title')"
    :subtitle="$t('gallery.edit_image_modal.subtitle')"
    :save-text="$t('gallery.edit_image_modal.save')"
    :loading="loading"
    @submit="saveImage($event, get('image').id)"
  >
    <!-- Alert Banner -->
    <v-alert
      v-if="!get('image').published"
      type="warning"
      text
      dense
      class="mb-4"
      icon="mdi-alert"
    >
      {{ $t('gallery.edit_image_modal.not_published_warning') }}
    </v-alert>

    <!-- Tabs -->
    <v-tabs
      v-model="activeTab"
      color="primary"
      class="mb-6 rounded-lg d-flex justify-center"
      height="48"
    >
      <v-tab class="text-none font-weight-bold">{{ $t('gallery.edit_image_modal.tab_details') }}</v-tab>
      <v-tab class="text-none font-weight-bold">{{ $t('gallery.edit_image_modal.tab_monetization') }}</v-tab>
      <v-tab class="text-none font-weight-bold">
        {{ $t('gallery.edit_image_modal.tab_visibility') }}
        <v-badge
          v-if="!get('image').published"
          color="error"
          dot
          inline
          class="ml-1"
        ></v-badge>
      </v-tab>
    </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- Tab 1: Details -->
        <v-tab-item>
          <v-card flat>
            <v-card-text class="px-0">
              <!-- Titel -->
              <div class="mb-0">
                <div class="text-body-2 font-weight-medium mb-2">{{ $t('gallery.edit_image_modal.title_label') }}</div>
                <v-text-field
                  v-model="get('image').title"
                  name="title"
                  :placeholder="$t('gallery.edit_image_modal.title_placeholder')"
                  :rules="imageNameRules"
                  outlined
                  required
                  dense
                  counter="50"
                  hide-details="auto"
                />
              </div>

              <!-- Beschreibung -->
              <div>
                <div class="text-body-2 font-weight-medium mb-2">{{ $t('gallery.edit_image_modal.description_label') }}</div>
                <v-textarea
                  v-model="get('image').description"
                  name="description"
                  :placeholder="$t('gallery.edit_image_modal.description_placeholder')"
                  outlined
                  dense
                  :rules="imgDescriptionRules"
                  counter="2000"
                  hide-details="auto"
                  rows="4"
                ></v-textarea>
              </div>

              <!-- Altersfreigabe -->
              <div class="mb-2">
                <div class="text-body-2 font-weight-medium mb-2">{{ $t('gallery.edit_image_modal.age_rating_label') }}</div>
                <v-select
                  name="fsk"
                  v-model="get('image').fsk"
                  :items="fskItems"
                  item-text="Altersfreigabe"
                  item-value="id"
                  :placeholder="$t('gallery.edit_image_modal.age_rating_placeholder')"
                  outlined
                  dense
                  hide-details="auto"
                ></v-select>
              </div>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <!-- Tab 2: Monetarisierung -->
        <v-tab-item>
          <v-card flat>
            <v-card-text class="px-0">
              <!-- Zugangsmodell -->
              <div class="mb-6">
                <div class="text-body-2 font-weight-medium mb-2">{{ $t('gallery.edit_image_modal.access_model') }}</div>

                <v-card
                  outlined
                  class="pa-4 mb-4"
                >
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="text-body-2 font-weight-bold mb-1">{{ $t('gallery.edit_image_modal.in_subscription') }}</div>
                      <div class="text-caption grey--text">{{ $t('gallery.edit_image_modal.in_subscription_hint') }}</div>
                    </div>
                    <v-switch
                      v-model="get('image').subscription"
                      name="subscription"
                      color="primary"
                      hide-details
                      class="mt-0"
                    ></v-switch>
                  </div>
                </v-card>

                <!-- Abo-Pläne (wenn Abo aktiv) -->
                <div v-if="get('image').subscription">
                  <div class="mb-3">
                    <div class="text-body-2 font-weight-medium mb-2">{{ $t('gallery.edit_image_modal.available_plans') }}</div>
                    <v-select
                      multiple
                      name="plans"
                      v-model="get('image').plans"
                      :items="get('plans')"
                      item-text="name"
                      item-value="id"
                      :placeholder="$t('gallery.edit_image_modal.plans_placeholder')"
                      :rules="[(v) => (v && v.length > 0) || this.$t('gallery.edit_image_modal.plans_required')]"
                      outlined
                      dense
                      chips
                      hide-details="auto"
                    >
                      <template v-slot:selection="{ item }">
                        <v-chip
                          color="primary"
                          small
                          close
                          @click:close="deleteItem(item)"
                        >
                          {{ item.name }}
                        </v-chip>
                      </template>
                    </v-select>
                    <div class="text-caption grey--text mt-1">{{ $t('gallery.edit_image_modal.plans_hint') }}</div>
                  </div>

                  <v-alert
                    type="info"
                    text
                    dense
                    icon="mdi-information-outline"
                  >
                    {{ $t('gallery.edit_image_modal.plans_info') }}
                  </v-alert>
                </div>

                <!-- Einzelpreis (wenn nicht im Abo) -->
                <div v-else>
                  <div class="text-body-2 font-weight-medium mb-2">{{ $t('gallery.edit_image_modal.single_price_label') }}</div>
                  <v-text-field
                    v-model="get('image').price"
                    name="price"
                    :placeholder="$t('gallery.edit_image_modal.price_placeholder')"
                    prefix="€"
                    :rules="priceRules"
                    outlined
                    dense
                    hide-details="auto"
                  ></v-text-field>
                  <div class="text-caption grey--text mt-1">{{ $t('gallery.edit_image_modal.price_free_or_min') }}</div>
                </div>
              </div>

            </v-card-text>
          </v-card>
        </v-tab-item>

        <!-- Tab 3: Sichtbarkeit -->
        <v-tab-item>
          <v-card flat>
            <v-card-text class="px-0">
              <!-- Sichtbarkeit Section -->
              <div class="mb-2">
                <div class="text-body-2 font-weight-medium mb-2">{{ $t('gallery.edit_image_modal.visibility_label') }}</div>

                <v-card
                  outlined
                  class="pa-4"
                >
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="text-body-2 font-weight-bold mb-1">
                        {{ $t('gallery.edit_image_modal.publish') }}
                        <v-icon
                          v-if="get('image').published"
                          color="success"
                          small
                          class="ml-2"
                        >mdi-check-circle</v-icon>
                      </div>
                      <div class="text-caption grey--text">{{ $t('gallery.edit_image_modal.publish_hint') }}</div>
                    </div>
                    <v-switch
                      v-model="get('image').published"
                      name="published"
                      color="primary"
                      hide-details
                      class="mt-0"
                    ></v-switch>
                  </div>
                </v-card>

              </div>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>

  </base-modal>
</template>

<script>
import APIService from '@/services/api.service';
import { mapGetters } from 'vuex';
import AppService from '@/services/app.service';

export default {
	name: 'editImageModal',

	components: {
		BaseModal: () => import('@/components/modal/baseModal')
	},

	data(){
		return {
			activeTab: 0,
			galleries: '',
			plan: '',
			published: false,
			loading: false,
			valid: false,
			galleryChoiceMenu: false,
			selectedGalleries: [],
			imageNameRules: [
				v => !!v || this.$t('gallery.edit_image_modal.validation.name_required'),
				v => (v && v.length <= 50) || this.$t('gallery.edit_image_modal.validation.name_max_length'),
				v => (v && v.length >= 2) || this.$t('gallery.edit_image_modal.validation.name_min_length')
			],
			imgDescriptionRules: [
				v => !v || v.length <= 2000 || this.$t('gallery.edit_image_modal.validation.description_max_length'),
			],
			priceRules: [
				v => !isNaN(v - 0) || this.$t('gallery.edit_image_modal.validation.price_invalid'),
				v => v >= 0 || this.$t('gallery.edit_image_modal.validation.price_positive')
			],

		}
	},

	computed:{
		...mapGetters(["get"]),
		fskItems() {
			return [
				{ id: 0, fsk: this.$t('gallery.edit_image_modal.fsk_none') },
				{ id: 16, fsk: this.$t('gallery.edit_image_modal.fsk_16') },
				{ id: 18, fsk: this.$t('gallery.edit_image_modal.fsk_18') }
			]
		},
		hasGalleryInside() {
			if(this.$store.state.main.galleries.result != undefined) {
			const galleries = this.$store.state.main.galleries.result;
			return imageId => {
				const foundGallery = galleries.find(gallery => gallery.images.some(image => image.id === imageId));
				return foundGallery ? foundGallery.title : this.$t('gallery.edit_image_modal.no_gallery_assigned');
			};
			}
			return this.$t('gallery.edit_image_modal.no_data_available')
		},
	},

	methods: {
		
		deleteItem(item){
			this.$store.dispatch('delete', { endpoint: `/api/v1/images/${this.$store.state.main.image.result.id}/plans/${item.id}`})
			.then(() => {
				this.$store.dispatch('fetch', { endpoint: `/api/v1/images?sort=DESC`, state: 'image'});
			})
		},

		saveImage(e, id){
			this.loading = true;
			let object = APIService.getFormData( APIService.getForm(e));
			object = AppService.formatPlans(object, 'image');
			object.fsk = parseInt(object.fsk)
			this.$store.dispatch('patch',
			{
				endpoint: `/api/v1/images/${id}`,
				state: 'image',
				event: object,
				message: false
			}).then(() => {
				this.loading = false;
				this.$store.dispatch('fetch', { endpoint: `/api/v1/images/${id}`, state: 'image'})
				this.$store.dispatch('fetch', { endpoint: '/api/v1/images?sort=DESC', state: 'images'});

				APIService.setResponse({ message: this.$t('gallery.edit_image_modal.response.saved'), color: "green" })
				this.$store.dispatch("modal/close", 'editImageModal')
			})
			
			
		},

		close(name) {
			this.$store.dispatch("modal/close", name)
		}
	}
}
</script>

<style scoped>
.gradient-avatar {
  background: linear-gradient(135deg, #00C3FF 0%, #0099CC 100%) !important;
}

/* Tab styling */
::v-deep .v-tabs {
  border-radius: 10px !important;
}

::v-deep .v-tab {
  text-transform: none !important;
}
</style>
